import { UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NotificationResponse } from 'src/generated/services/TFinancialApi';

import { tFinancialService } from 'src/infra/services/tFinancialService';
import { cacheKeysUtil } from 'src/utils/cacheKeysUtil';
import { useSSE } from 'src/hooks/useSSE';
// Note: Real-time notification status is now managed through Redux SSE

/**
 * Hook for fetching notifications, optimized to only fetch when dropdown is open
 * @param isDropdownOpen - Whether the notification dropdown is open
 * @param viewDate - Optional date filter
 * @returns Notification query result with additional methods
 */
export const useGetNotifications = (
  isDropdownOpen: boolean = false,
  viewDate: string = null
): UseQueryResult<NotificationResponse> & { markAsRead: (id: number) => Promise<void>; markAllAsRead: () => Promise<void> } => {
  const queryClient = useQueryClient();
  const { markAllAsRead: markAllSSEAsRead } = useSSE();
  const query: { viewDate?: string } = {};
  if (viewDate) {
    query.viewDate = viewDate;
  }

  const queryResult = useQuery(
    cacheKeysUtil.getNotifications(),
    async (): Promise<NotificationResponse> => (await tFinancialService.api.getNotifications(query)).data.data,
    {
      // Only fetch when dropdown is open
      enabled: isDropdownOpen,
      // No continuous polling - we'll rely on WebSocket for status updates
      refetchInterval: false,
      // Refetch when window regains focus if dropdown is open
      refetchOnWindowFocus: isDropdownOpen,
      // Disable caching to ensure fresh data
      cacheTime: 0,
      keepPreviousData: false,
      staleTime: 0,
    }
  );

  // Mark notification as read
  const markAsRead = async (id: number) => {
    try {
      // Optimistically update UI
      queryClient.setQueryData(cacheKeysUtil.getNotifications(), (oldData: any) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          notifications: oldData.notifications.map((notification: any) => (notification.id === id ? { ...notification, read: true } : notification)),
        };
      });

      // Call the API to ensure server-side persistence
      await tFinancialService.api.markNotificationAsRead();

      // Update SSE local state after successful API call
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Revert optimistic update on error
      queryClient.invalidateQueries(cacheKeysUtil.getNotifications());
    }
  };

  // Mark all notifications as read
  const markAllAsRead = async () => {
    try {
      // ✅ FIX: Update Redux immediately (optimistic) so red dot disappears on first click
      markAllSSEAsRead();

      // Optimistically update React Query cache
      queryClient.setQueryData(cacheKeysUtil.getNotifications(), (oldData: any) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          notifications: oldData.notifications.map((notification: any) => ({ ...notification, read: true })),
        };
      });

      // Call the API to ensure server-side persistence
      await tFinancialService.api.markNotificationAsRead();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      // Revert optimistic updates on error
      queryClient.invalidateQueries(cacheKeysUtil.getNotifications());
      // TODO: Consider adding Redux revert here if needed
    }
  };

  return {
    ...queryResult,
    markAsRead,
    markAllAsRead,
  };
};

export const useGetNotificationsByDate = (viewDate: string = null): UseQueryResult<NotificationResponse> => {
  const query: { viewDate?: string } = {};
  if (viewDate) {
    query.viewDate = viewDate;
  }
  return useQuery(
    cacheKeysUtil.getNotifications(),
    async (): Promise<NotificationResponse> => (await tFinancialService.api.getNotifications(query)).data.data,
    {
      enabled: !!viewDate,
      cacheTime: 0,
      keepPreviousData: false,
    }
  );
};

/**
 * Legacy hook for marking notifications as read
 * @deprecated Use the markAsRead method from useGetNotifications instead
 */
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation(async () => tFinancialService.api.markNotificationAsRead(), {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSuccess: async (_, args) => {
      await Promise.all([queryClient.invalidateQueries(cacheKeysUtil.getNotifications())]);
    },
  });
};
